import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { select } from 'd3-selection'
import { zoom as d3Zoom, zoomIdentity, type ZoomBehavior, type ZoomTransform } from 'd3-zoom'
import { drag as d3Drag } from 'd3-drag'
import { easeCubicInOut } from 'd3-ease'
import { linkVertical } from 'd3-shape'
import 'd3-transition'
import type { Simulation } from 'd3-force'
import type { MockEdge, MockNode, MockNodeType } from './types'
import { NODE_TYPE_STYLES, SELECTED_NODE_BG, SELECTED_NODE_FG } from './nodeIcons'
import type { GraphLayout } from './layoutOptions'
import { linkStrokeWidth } from './displaySettings'
import type { CanvasHandle, HoverInfo, Overview, Viewport } from './KnowledgeGraphCanvas'
import {
  buildModel,
  collapseToDepth,
  expandAll,
  labelFontSize,
  seedRadialPositions,
  toggleChildren,
  treePositions,
  visibleGraph,
  type GraphLinkDatum,
  type GraphModel,
  type GraphNodeDatum,
} from './d3/graphModel'
import { createSimulation, FORCE, replan, settle } from './d3/forces'

/**
 * The Knowledge Graph canvas in D3 (DSGN-63 radial spec).
 *
 * Radial View is a force layout computed to rest before it is painted (no
 * animation): every parent is a hub with its leaves bunched round it like a
 * star, the stars spread over one round cluster round the enterprise, cross
 * links faint and dashed. Tree View is a top-down tree with curved links
 * (leaf groups as grids beside their parent, cross links dashed). Clicking a
 * node lights it, its links and the nodes at their other ends, and fades the
 * rest; nothing moves. Collapsing and expanding re-lay the graph at once.
 *
 * It keeps the `CanvasHandle` contract of the cytoscape canvas so the minimap
 * and the zoom controls are unchanged.
 */
export interface D3CanvasHandle extends CanvasHandle {
  /** Re-seed and re-lay the current view from scratch, resetting selection and zoom. */
  replayEntrance: () => void
  collapseToSites: () => void
  expandAll: () => void
  /** Expand or collapse one node's children. Returns false for a leaf. */
  toggleChildren: (id: string) => boolean
  isCollapsed: (id: string) => boolean
  hasChildren: (id: string) => boolean
}

interface Props {
  nodes: MockNode[]
  edges: MockEdge[]
  layout?: GraphLayout
  enabledTypes: Set<MockNodeType>
  filteredOutIds?: Set<string>
  showEntityLabels?: boolean
  showEdgeLabels?: boolean
  showOrphans?: boolean
  /**
   * Draw a node with no link as an ordinary node: no parking beside the graph,
   * no hiding. The page sets this while the toolbar search is on, because a
   * search keeps only the matching nodes and most of them lose their parent —
   * they are the result, not orphans of it.
   */
  keepUnlinked?: boolean
  lineWidth?: number
  /**
   * Radial View's Spacing setting, in percent (100 = the layout's natural
   * spacing): every link, star and gap is this much longer; nodes and labels
   * keep their size. Tree View ignores it.
   */
  spacing?: number
  selectedNodeId?: string | null
  /**
   * A connection picked in the details panel. Its edge draws teal and both
   * ends take the selected look; matched by endpoints in either direction,
   * because a tree edge's drawn id (`parent>child`) is not the edge's own id.
   */
  selectedLink?: { source: string; target: string } | null
  onNodeSelect?: (nodeId: string | null) => void
  onNodeHover?: (info: HoverInfo | null) => void
}

/** Radial View zooms out as far as Tree View: the design frames a whole tenant's stars at 22%. */
const ZOOM_EXTENT: [number, number] = [0.05, 2.5]
/** Tree View zooms out further: a whole tenant's tree is many screens tall (the design frames it at 9%). */
const TREE_ZOOM_EXTENT: [number, number] = [0.05, 2.5]
/**
 * Tree View draws its cross links at rest only while there are this few, as
 * the design does; past that (a real tenant has hundreds of sheet pairs) they
 * are drawn only for the selected node and its parent, or the tree is lost
 * under the mesh.
 */
export const TREE_CROSS_REST_MAX = 40
/** Tree View draws the non-hierarchy connections (cross-sheet links) as the design does: straight, dashed, steel blue. */
const CROSS_LINK = { light: '#6b97ab', dark: '#7aa7bb' }
const CROSS_LINK_DASH = '6 4'
/** Radial View's cross links, sampled off the reference app at 100%: opaque steel blue, 9px dashes, 5px gaps. */
const RADIAL_CROSS_LINK = { light: '#609cae', dark: '#7aa7bb' }
export const RADIAL_CROSS_LINK_DASH = '9 5'
/** Parent → child lines in both views: the designs' grey (Radial sampled off the reference app at 100%), so the tree reads at 10% zoom. */
const TREE_LINK_GRAY = { light: '#a3a3a3', dark: '#737373' }
const ORPHAN_AREA_GAP = 160
const ORPHAN_AREA_ROW = 72
/**
 * The selected node's colours, sampled off the design: the ring and icon take
 * the teal, the disc a pale wash of it, and the label the same teal. Selection
 * recolours — it does NOT thicken the ring, which read as a heavy outline.
 */
const SELECTED_FG = SELECTED_NODE_FG
const SELECTED_BG = SELECTED_NODE_BG


/** Radial View draws a hub (a node with children) this much bigger than a leaf, as the design does. */
export const RADIAL_HUB_SCALE = 1.4
/** How much heavier the connection picked in the details panel is drawn than the selected node's other links. */
const PICKED_EXTRA_WIDTH = 1.5
/** The line from a selected node's parent up to ITS parent: where the branch hangs (design: dark grey). */
const CONTEXT_LINK = { light: '#525252', dark: '#d4d4d4' }

const isDark = () =>
  typeof document !== 'undefined' &&
  (document.documentElement.classList.contains('dark') ||
    (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches === true))

const treeGray = () => (isDark() ? TREE_LINK_GRAY.dark : TREE_LINK_GRAY.light)
const crossLinkBlue = () => (isDark() ? CROSS_LINK.dark : CROSS_LINK.light)
/** A cross link in Tree View (the one view that hides them when crowded). */
const isTreeCross = (l: GraphLinkDatum, layout: GraphLayout) => layout === 'tree' && !l.hierarchy
/** Both views draw a cross link dashed steel blue, and the tree's own links grey. */
const restingStroke = (l: GraphLinkDatum, layout: GraphLayout) =>
  !l.hierarchy
    ? layout === 'radial' ? (isDark() ? RADIAL_CROSS_LINK.dark : RADIAL_CROSS_LINK.light) : crossLinkBlue()
    : treeGray()
const restingDash = (l: GraphLinkDatum, layout: GraphLayout) => (l.hierarchy ? null : layout === 'radial' ? RADIAL_CROSS_LINK_DASH : CROSS_LINK_DASH)
/** Radial View's cross links are drawn faint, as in the design, so the hubs' stars read through them. */

/** A node and every ancestor above it. */
const ancestorIds = (model: GraphModel, id: string): Set<string> => {
  const out = new Set<string>()
  for (let cur = model.byId.get(id); cur && !out.has(cur.id); cur = cur.parentId ? model.byId.get(cur.parentId) : undefined) out.add(cur.id)
  return out
}

const mid = (l: GraphLinkDatum) => ({ x: (l.source.x + l.target.x) / 2, y: (l.source.y + l.target.y) / 2 })

const curvedLink = linkVertical<GraphLinkDatum, GraphNodeDatum>()
  .x((d) => d.x)
  .y((d) => d.y)

const straightLink = (l: GraphLinkDatum) => `M${l.source.x},${l.source.y}L${l.target.x},${l.target.y}`
/** Tree View curves the parent → child links only; cross links run straight between their ends. */
const linkPath = (l: GraphLinkDatum, layout: GraphLayout) => (layout === 'tree' && l.hierarchy ? curvedLink(l) : straightLink(l))

const KnowledgeGraphD3Canvas = forwardRef<D3CanvasHandle, Props>(function KnowledgeGraphD3Canvas(
  {
    nodes,
    edges,
    layout = 'radial',
    enabledTypes,
    filteredOutIds,
    showEntityLabels = true,
    showEdgeLabels = false,
    showOrphans = false,
    keepUnlinked = false,
    lineWidth = 1,
    spacing = 100,
    selectedNodeId = null,
    selectedLink = null,
    onNodeSelect,
    onNodeHover,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const modelRef = useRef<GraphModel | null>(null)
  const simRef = useRef<Simulation<GraphNodeDatum, GraphLinkDatum> | null>(null)
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const transformRef = useRef<ZoomTransform>(zoomIdentity)
  const sizeRef = useRef({ w: 800, h: 600 })
  const zoomSubs = useRef<Set<(z: number) => void>>(new Set())
  const overviewSubs = useRef<Set<(ov: Overview) => void>>(new Set())
  const overviewAtRef = useRef(0)
  /** What applyVisibility last dimmed and hid, for the minimap. */
  const shownRef = useRef<{ dimmed: Set<string>; hidden: Set<string> }>({ dimmed: new Set(), hidden: new Set() })
  const viewportSubs = useRef<Set<(vp: Viewport) => void>>(new Set())
  const selectedRef = useRef<string | null>(null)
  // The node under the pointer, whose connections are lit while it is hovered.
  const hoveredRef = useRef<string | null>(null)
  // The link drawn teal for the connection picked in the details panel.
  const pickedLinkIdRef = useRef<string | null>(null)
  // The layout the canvas is drawn in; the layout-switch effect advances it.
  const appliedLayoutRef = useRef<GraphLayout>(layout)
  // The Spacing the radial picture is drawn at; the spacing effect advances it.
  const appliedSpacingRef = useRef(spacing)
  /**
   * What the reader set up on the canvas (collapsed nodes, positions, camera),
   * kept when the canvas is torn down. Tree View draws its own links (one per
   * pair of sheets), so a view switch rebuilds the canvas; the rebuild restores
   * these so the switch keeps them as it does for a graph both views share.
   */
  const carryRef = useRef<{ collapsed: Set<string>; transform: ZoomTransform } | null>(null)
  const selectedNodeIdRef = useRef(selectedNodeId)
  selectedNodeIdRef.current = selectedNodeId
  const selectedLinkRef = useRef<{ source: string; target: string } | null>(selectedLink)
  selectedLinkRef.current = selectedLink
  // Latest props, read by handlers that live across renders.
  const propsRef = useRef({ layout, enabledTypes, filteredOutIds, showEntityLabels, showEdgeLabels, showOrphans, keepUnlinked, lineWidth, spacing, onNodeSelect, onNodeHover })
  propsRef.current = { layout, enabledTypes, filteredOutIds, showEntityLabels, showEdgeLabels, showOrphans, keepUnlinked, lineWidth, spacing, onNodeSelect, onNodeHover }

  const layers = () => {
    const svg = select(svgRef.current as SVGSVGElement)
    const zoomG = svg.select<SVGGElement>('g.zoom')
    return {
      svg,
      zoomG,
      links: zoomG.select<SVGGElement>('g.links'),
      effects: zoomG.select<SVGGElement>('g.effects'),
      nodes: zoomG.select<SVGGElement>('g.nodes'),
    }
  }

  const viewport = (): Viewport => {
    const t = transformRef.current
    const { w, h } = sizeRef.current
    return { x1: (-w / 2 - t.x) / t.k, y1: (-h / 2 - t.y) / t.k, x2: (w / 2 - t.x) / t.k, y2: (h / 2 - t.y) / t.k }
  }
  /** The graph point at the middle of the screen. */
  const viewCentre = () => {
    const vp = viewport()
    return { x: (vp.x1 + vp.x2) / 2, y: (vp.y1 + vp.y2) / 2 }
  }
  const notifyViewport = () => {
    const vp = viewport()
    viewportSubs.current.forEach((fn) => fn(vp))
    zoomSubs.current.forEach((fn) => fn(transformRef.current.k))
  }

  const buildOverview = (): Overview | null => {
    const model = modelRef.current
    if (!model) return null
    const { nodes: all, links: allLinks } = visibleGraph(model)
    // The thumbnail draws what the canvas draws: no hidden orphan, and what a
    // filter dims, faint (applyVisibility keeps both sets).
    const { dimmed, hidden } = shownRef.current
    const vis = all.filter((d) => !hidden.has(d.id))
    const links = allLinks.filter((l) => !hidden.has(l.source.id) && !hidden.has(l.target.id))
    const linkDimmed = (l: GraphLinkDatum) => dimmed.has(l.source.id) || dimmed.has(l.target.id)
    return {
      nodes: vis.map((d) => ({ id: d.id, x: d.x, y: d.y, type: d.type, dimmed: dimmed.has(d.id) })),
      // The thumbnail leaves out the cross links Tree View is not drawing at rest.
      edges: (crowdedTreeCross(links) ? links.filter((l) => l.hierarchy) : links).map((l) => ({
        source: l.source.id,
        target: l.target.id,
        dimmed: linkDimmed(l),
      })),
      bbox: bbox(vis.length > 0 ? vis : all),
    }
  }
  /** Tell the minimap where the nodes are: at once when a layout is shown or
   *  the graph changes, and ~12 times a second while the physics moves them,
   *  so the thumbnail follows the main view. */
  const notifyOverview = (force = false) => {
    if (overviewSubs.current.size === 0) return
    const now = Date.now()
    if (!force && now - overviewAtRef.current < 80) return
    overviewAtRef.current = now
    const ov = buildOverview()
    if (ov) overviewSubs.current.forEach((fn) => fn(ov))
  }

  /** Positions → DOM. The simulation's tick handler, also called once by hand. */
  const tick = () => {
    const { links, nodes: nodesG } = layers()
    const layout = propsRef.current.layout
    links.selectAll<SVGPathElement, GraphLinkDatum>('path.link').attr('d', (l) => linkPath(l, layout))
    links
      .selectAll<SVGTextElement, GraphLinkDatum>('text.link-label')
      .attr('x', (l) => mid(l).x)
      .attr('y', (l) => mid(l).y - 4)
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').attr('transform', (d) => `translate(${d.x},${d.y})`)
    notifyOverview()
  }

  const bbox = (ds: GraphNodeDatum[]) => {
    if (ds.length === 0) return { x1: -1, y1: -1, x2: 1, y2: 1 }
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
    for (const d of ds) {
      x1 = Math.min(x1, d.x - d.r); y1 = Math.min(y1, d.y - d.r); x2 = Math.max(x2, d.x + d.r); y2 = Math.max(y2, d.y + d.r + 18)
    }
    return { x1, y1, x2, y2 }
  }

  /** Orphans: nodes with no link on the canvas, parked in a column beside it,
   *  or hidden. A filter does not make one: a type or uploader filtered out
   *  dims its nodes and their links, and the nodes on the other end stay
   *  where they are (Slack C0A52ABLSPK, 2026-10-24: turning Functional
   *  Location off took every equipment with it, all "orphans" of their dimmed
   *  sheet, and left the canvas empty).
   *  The toolbar search is not applied here: the page hands the canvas the
   *  graph already narrowed to the matches, so a search re-lays out what is
   *  left rather than dimming. */
  const applyVisibility = () => {
    const model = modelRef.current
    if (!model) return
    const { enabledTypes: types, filteredOutIds: out, showOrphans: orphansOn, keepUnlinked, showEntityLabels: labelsOn, showEdgeLabels: edgeLabelsOn, lineWidth: setting } = propsRef.current
    const width = linkStrokeWidth(setting)
    const { nodes: vis, links } = visibleGraph(model)
    const dimmed = new Set<string>()
    for (const d of vis) {
      const typeOk = types.size === 0 || types.has(d.type)
      const facetOk = !out?.has(d.id)
      if (!(typeOk && facetOk)) dimmed.add(d.id)
    }
    const linkDimmed = (l: GraphLinkDatum) => dimmed.has(l.source.id) || dimmed.has(l.target.id)
    const connected = new Set<string>()
    for (const l of links) { connected.add(l.source.id); connected.add(l.target.id) }
    const orphans = keepUnlinked ? [] : vis.filter((d) => d.type !== 'enterprise' && !connected.has(d.id))
    const orphanIds = new Set(orphans.map((d) => d.id))
    if (orphansOn && orphans.length > 0) {
      const rest = vis.filter((d) => !orphanIds.has(d.id))
      const bb = bbox(rest)
      let y = bb.y1
      for (const d of orphans) { d.fx = bb.x2 + ORPHAN_AREA_GAP; d.fy = y; d.x = d.fx; d.y = d.fy; y += ORPHAN_AREA_ROW }
    } else {
      for (const d of orphans) { if (d.fx !== undefined && d.fx !== null) { d.fx = null; d.fy = null } }
    }
    const { links: linksG, nodes: nodesG } = layers()
    nodesG
      .selectAll<SVGGElement, GraphNodeDatum>('g.node')
      .classed('dimmed', (d) => dimmed.has(d.id))
      .classed('orphan', (d) => orphanIds.has(d.id))
      .style('display', (d) => (orphanIds.has(d.id) && !orphansOn ? 'none' : 'inline'))
      .select('text.label')
      .style('display', labelsOn ? 'inline' : 'none')
    linksG
      .selectAll<SVGPathElement, GraphLinkDatum>('path.link')
      .classed('dimmed', (l) => linkDimmed(l) || orphanIds.has(l.source.id) || orphanIds.has(l.target.id))
      .style('display', (l) => ((orphanIds.has(l.source.id) || orphanIds.has(l.target.id)) && !orphansOn ? 'none' : 'inline'))
    linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').style('display', edgeLabelsOn ? 'inline' : 'none')
    linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link').each(function () {
      const el = select(this)
      if (!el.classed('picked')) el.attr('stroke-width', width)
    })
    shownRef.current = { dimmed, hidden: orphansOn ? new Set() : orphanIds }
    styleLinks()
    tick()
    // A filter changed what is faint or hidden: the thumbnail follows at once,
    // not after the tick's rate limit.
    notifyOverview(true)
  }

  /** Resting link colour and dash for the current layout; a picked link keeps its teal. */
  const styleLinks = () => {
    const layout = propsRef.current.layout
    const { links: linksG } = layers()
    linksG
      .selectAll<SVGPathElement, GraphLinkDatum>('path.link')
      .attr('stroke-dasharray', (l) => restingDash(l, layout))
      .filter(function () { return !select(this).classed('picked') && !select(this).classed('lit') })
      .attr('stroke', (l) => restingStroke(l, layout))
    hideCrowdedCrossLinks()
  }

  /** Past TREE_CROSS_REST_MAX, Tree View keeps only the cross links at the selected node or its parent. */
  const hideCrowdedCrossLinks = () => {
    const model = modelRef.current
    if (!model) return
    const layout = propsRef.current.layout
    const { links: linksG } = layers()
    const paths = linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link')
    const crowded = crowdedTreeCross(paths.data())
    // The hovered node's cross links show while it is hovered, as the selection's do.
    const sel = hoveredRef.current ?? selectedRef.current
    const near = new Set<string>()
    if (sel) {
      near.add(sel)
      const parent = model.byId.get(sel)?.parentId
      if (parent) near.add(parent)
    }
    const hidden = (l: GraphLinkDatum) =>
      crowded && isTreeCross(l, layout) && l.id !== pickedLinkIdRef.current && !near.has(l.source.id) && !near.has(l.target.id)
    // An attribute, not `display`: applyVisibility owns the inline display (orphans).
    paths.attr('visibility', (l) => (hidden(l) ? 'hidden' : null))
    linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').attr('visibility', (l) => (hidden(l) ? 'hidden' : null))
  }

  /** Tree View has more cross links than it draws at rest. */
  const crowdedTreeCross = (links: GraphLinkDatum[]): boolean => {
    const layout = propsRef.current.layout
    let n = 0
    for (const l of links) if (isTreeCross(l, layout)) n += 1
    return n > TREE_CROSS_REST_MAX
  }

  const zoomExtent = (): [number, number] => (propsRef.current.layout === 'tree' ? TREE_ZOOM_EXTENT : ZOOM_EXTENT)

  /** Click on a node (DSGN-63, "image copy 5"): the selected node is
   *  recoloured teal (ring, icon and label; the ring KEEPS its 1.5 width), the
   *  links at it are drawn teal and the nodes at their other ends stay lit,
   *  its parent's own link up the tree is drawn dark grey, and everything else
   *  fades. No camera move, no ripple or pulse. A connection picked in the
   *  panel adds its edge, drawn teal, and its other end, which takes the same
   *  look as the selected node. */
  const applyFocus = (id: string | null) => {
    if (!modelRef.current) return
    selectedRef.current = id
    hoveredRef.current = null
    drawFocus(id)
  }

  /** Draws the focus look round `id` without changing the selection. Hovering
   *  a node draws it round the hovered node ("image copy 12"); leaving draws
   *  it round the selection again. The picked connection belongs to the
   *  selection, so a hover over any other node leaves it out. */
  const drawFocus = (id: string | null, opts: { hover?: boolean } = {}) => {
    const model = modelRef.current
    if (!model) return
    const { nodes: nodesG, links: linksG } = layers()
    const sel = id ? model.byId.get(id) ?? null : null
    const pick = sel && sel.id === selectedRef.current ? selectedLinkRef.current : null
    const exact = (l: GraphLinkDatum): boolean =>
      !!pick &&
      ((l.source.id === pick.source && l.target.id === pick.target) || (l.source.id === pick.target && l.target.id === pick.source))
    const linkSel = linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link')
    // Tree View draws a tag-to-tag connection as one link between the two
    // ends' sheets (treeViewGraph); a picked connection lights that link.
    let picked = linkSel.data().find(exact)
    if (pick && !picked && propsRef.current.layout === 'tree') {
      const a = ancestorIds(model, pick.source)
      const b = ancestorIds(model, pick.target)
      picked = linkSel.data().find((l) =>
        !l.hierarchy && ((a.has(l.source.id) && b.has(l.target.id)) || (b.has(l.source.id) && a.has(l.target.id))))
    }
    pickedLinkIdRef.current = picked?.id ?? null
    const isPicked = (l: GraphLinkDatum): boolean => !!picked && l.id === picked.id
    const incident = (l: GraphLinkDatum): boolean => !!sel && (l.source.id === sel.id || l.target.id === sel.id)
    const parent = sel?.parentId ? model.byId.get(sel.parentId) : undefined
    const contextId = parent?.parentId ? `${parent.parentId}>${parent.id}` : null
    const isContext = (l: GraphLinkDatum): boolean => l.id === contextId && !incident(l)
    const shown = (l: GraphLinkDatum): boolean => isPicked(l) || incident(l) || isContext(l)
    const near = new Set<string>()
    if (sel) {
      near.add(sel.id)
      for (const l of linkSel.data()) if (incident(l)) { near.add(l.source.id); near.add(l.target.id) }
    }
    if (picked) { near.add(picked.source.id); near.add(picked.target.id) }
    linkSel
      .classed('picked', isPicked)
      .classed('lit', (l) => !isPicked(l) && shown(l))
      .classed('faded', (l) => !!sel && !shown(l))
      .attr('stroke', (l) =>
        isPicked(l) || incident(l)
          ? SELECTED_FG
          : isContext(l)
            ? (isDark() ? CONTEXT_LINK.dark : CONTEXT_LINK.light)
            : restingStroke(l, propsRef.current.layout))
      // The connection picked in the panel is the one teal line drawn heavier.
      .attr('stroke-width', (l) => linkStrokeWidth(propsRef.current.lineWidth) + (isPicked(l) ? PICKED_EXTRA_WIDTH : 0))
    // Drawn last so no faded edge crosses over them.
    linkSel.filter(shown).raise()
    const lit = new Set<string>()
    if (sel) lit.add(sel.id)
    if (pick) { lit.add(pick.source); lit.add(pick.target) }
    const nodeSel = nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node')
    nodeSel.classed('selected', (d) => lit.has(d.id))
    nodeSel.classed('faded', (d) => !!sel && !near.has(d.id) && !lit.has(d.id))
    nodeSel.classed('near', (d) => !!sel && near.has(d.id) && !lit.has(d.id))
    // A hover reorders no node: re-inserting the node under the pointer, or
    // raising a neighbour dragged on top of it, can fire mouseleave/mouseenter
    // again and flicker the highlight between them.
    if (!opts.hover) nodeSel.filter((d) => near.has(d.id) || lit.has(d.id)).raise()
    linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').classed('faded', (l) => !!sel && !shown(l))
    const isSel = (d: GraphNodeDatum): boolean => lit.has(d.id)
    nodeSel
      .select('circle')
      .attr('fill', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_BG : NODE_TYPE_STYLES[d.type].bg))
      .attr('stroke', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_FG : NODE_TYPE_STYLES[d.type].ring))
      .attr('stroke-width', 1.5)
    nodeSel
      .select('svg.icon')
      .attr('stroke', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_FG : NODE_TYPE_STYLES[d.type].fg))
      .attr('color', (d: GraphNodeDatum) => (isSel(d) ? SELECTED_FG : NODE_TYPE_STYLES[d.type].fg))
    hideCrowdedCrossLinks()
  }

  /** Data join for the visible graph. Both views are static: nodes and links
   *  appear and go at once, with no fade or grow (the design has no animation). */
  const render = () => {
    const model = modelRef.current
    if (!model) return
    const { links: linksG, nodes: nodesG } = layers()
    const { nodes: vis, links } = visibleGraph(model)
    const layout = propsRef.current.layout
    const width = linkStrokeWidth(propsRef.current.lineWidth)

    const link = linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link').data(links, (l) => l.id)
    link.exit().remove()
    link
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('fill', 'none')
      .attr('stroke', (l) => restingStroke(l, layout))
      .attr('stroke-dasharray', (l) => restingDash(l, layout))
      .attr('stroke-width', width)
      .attr('d', (l) => linkPath(l, layout))
    const label = linksG.selectAll<SVGTextElement, GraphLinkDatum>('text.link-label').data(links.filter((l) => l.label || l.hierarchy), (l) => l.id)
    label.exit().remove()
    label
      .enter()
      .append('text')
      .attr('class', 'link-label fill-neutral-500 dark:fill-neutral-400')
      .attr('font-size', 10)
      .attr('text-anchor', 'middle')
      .text((l) => l.label ?? (l.source.type === 'enterprise' ? 'has site' : l.target.type === 'documents' ? 'documented by' : 'contains'))

    const node = nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').data(vis, (d) => d.id)
    node.exit().remove()
    const enter = node.enter().append('g').attr('class', 'node').attr('transform', (d) => `translate(${d.x},${d.y})`).style('cursor', 'pointer')
    const inner = enter.append('g').attr('class', 'inner')
    inner
      .append('circle')
      .attr('r', (d) => d.r)
      .attr('fill', (d) => NODE_TYPE_STYLES[d.type].bg)
      .attr('stroke', (d) => NODE_TYPE_STYLES[d.type].ring)
      .attr('stroke-width', 1.5)
    inner
      .append('svg')
      .attr('class', 'icon')
      .attr('viewBox', '0 0 24 24')
      .attr('fill', 'none')
      .attr('stroke-width', 2)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('stroke', (d) => NODE_TYPE_STYLES[d.type].fg)
      // Filled (Energy Icons) markup paints with currentColor.
      .attr('color', (d) => NODE_TYPE_STYLES[d.type].fg)
      .attr('width', (d) => d.r * 1.05)
      .attr('height', (d) => d.r * 1.05)
      .attr('x', (d) => -d.r * 0.525)
      .attr('y', (d) => -d.r * 0.525)
      .html((d) => NODE_TYPE_STYLES[d.type].iconPaths)
    inner
      .append('text')
      .attr('class', 'label fill-neutral-500 dark:fill-neutral-400')
      .attr('text-anchor', 'middle')
      .attr('y', (d) => d.r + 14)
      .attr('font-size', (d) => labelFontSize(d.depth))
      .text((d) => d.displayLabel)
    // The full name for a truncated label, as the native tooltip.
    enter.append('title').text((d) => d.label)
    inner.attr('transform', (d) => `scale(${d.s})`)
    enter
      .on('click', (event: MouseEvent, d) => {
        event.stopPropagation()
        // A node click drops the picked connection; the page clears its own copy.
        selectedLinkRef.current = null
        propsRef.current.onNodeSelect?.(d.id)
        applyFocus(d.id)
      })
      .on('mouseenter', function (_event, d) {
        if (hoveredRef.current !== d.id) {
          hoveredRef.current = d.id
          drawFocus(d.id, { hover: true })
        }
        if (!propsRef.current.onNodeHover || !svgRef.current) return
        const t = transformRef.current
        const { w, h } = sizeRef.current
        const rect = svgRef.current.getBoundingClientRect()
        const sx = rect.width / (w || 1), sy = rect.height / (h || 1)
        propsRef.current.onNodeHover({ id: d.id, x: (t.applyX(d.x) + w / 2 + d.r * t.k) * sx, y: (t.applyY(d.y) + h / 2) * sy })
      })
      .on('mouseleave', (_event, d) => {
        if (hoveredRef.current === d.id) {
          hoveredRef.current = null
          drawFocus(selectedRef.current)
        }
        propsRef.current.onNodeHover?.(null)
      })
      .call(
        d3Drag<SVGGElement, GraphNodeDatum>()
          // Moves the dragged node alone, with its links; the rest of the
          // graph does not move (no physics runs on screen).
          .on('drag', (event, d) => {
            if (propsRef.current.layout !== 'radial') return
            d.x = event.x; d.y = event.y
            if (d.fx != null) { d.fx = event.x; d.fy = event.y }
            tick()
          })
          .on('end', () => notifyOverview(true)),
      )
    // Collapsed nodes get a dashed ring.
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').select('circle').attr('stroke-dasharray', (d) => (d.collapsed ? '3 2' : null))

    // Feed the simulation the visible set, with the plan for it.
    const sim = simRef.current
    if (sim) {
      sim.nodes(vis)
      replan(sim, links, spacingFactor())
    }
    restScales()
    applyVisibility()
    notifyOverview(true)
  }

  /** Tree View: stop the physics and place the tree at once (no animation),
   *  laid out around where the reader is looking so the view never has to move. */
  const layoutTree = () => {
    const model = modelRef.current
    if (!model) return
    simRef.current?.stop()
    const pos = treePositions(model)
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
    pos.forEach((p) => { x1 = Math.min(x1, p.x); y1 = Math.min(y1, p.y); x2 = Math.max(x2, p.x); y2 = Math.max(y2, p.y) })
    const vp = viewport()
    const dx = pos.size ? (vp.x1 + vp.x2) / 2 - (x1 + x2) / 2 : 0
    const dy = pos.size ? (vp.y1 + vp.y2) / 2 - (y1 + y2) / 2 : 0
    for (const d of model.nodes) {
      const p = pos.get(d.id)
      if (p) { d.x = p.x + dx; d.y = p.y + dy; d.vx = 0; d.vy = 0 }
    }
    const { links: linksG, nodes: nodesG } = layers()
    nodesG.selectAll<SVGGElement, GraphNodeDatum>('g.node').interrupt('layout')
    linksG.selectAll<SVGPathElement, GraphLinkDatum>('path.link').interrupt('layout')
    tick()
    notifyOverview(true)
  }

  const fitTo = (animate: boolean) => {
    const model = modelRef.current
    const zoomB = zoomRef.current
    if (!model || !zoomB || !svgRef.current) return
    const { nodes: vis } = visibleGraph(model)
    const bb = bbox(vis)
    const { w, h } = sizeRef.current
    const pad = 60
    const [kMin, kMax] = zoomExtent()
    const k = Math.max(kMin, Math.min(kMax, Math.min((w - pad * 2) / Math.max(bb.x2 - bb.x1, 1), (h - pad * 2) / Math.max(bb.y2 - bb.y1, 1))))
    const cx = (bb.x1 + bb.x2) / 2, cy = (bb.y1 + bb.y2) / 2
    const t = zoomIdentity.scale(k).translate(-cx, -cy)
    const svg = select(svgRef.current)
    if (animate) svg.transition().duration(400).ease(easeCubicInOut).call(zoomB.transform, t)
    else svg.call(zoomB.transform, t)
  }

  /** Rest scale per view: Radial View draws hubs RADIAL_HUB_SCALE larger; Tree View draws every node at 1. */
  const restScales = () => {
    const model = modelRef.current
    if (!model) return
    const radial = propsRef.current.layout === 'radial'
    for (const d of model.nodes) d.s = radial && d.childIds.length > 0 ? RADIAL_HUB_SCALE : 1
    layers().nodes.selectAll<SVGGElement, GraphNodeDatum>('g.node').select('g.inner').attr('transform', (d) => `scale(${d.s})`)
  }

  /**
   * Radial View at rest, computed before anything moves on screen (the
   * design has no entrance animation). `fresh` seeds the graph in its settled
   * shape around the root and settles it; otherwise it settles from where the
   * nodes are (a collapse or an expand). `at` puts the root at that point.
   */
  /** The Spacing setting as the factor the radial plan takes. */
  const spacingFactor = () => propsRef.current.spacing / 100

  const layoutRadial = (fresh: boolean, at?: { x: number; y: number }) => {
    const model = modelRef.current
    const sim = simRef.current
    if (!model || !sim) return
    restScales()
    const { nodes: vis, links } = visibleGraph(model)
    sim.nodes(vis)
    replan(sim, links, spacingFactor())
    if (fresh) seedRadialPositions(model, spacingFactor())
    settle(sim, fresh ? FORCE.settleAlpha : FORCE.resettleAlpha)
    if (fresh && at) {
      for (const d of model.nodes) {
        d.x += at.x; d.y += at.y
        if (d.fx != null) d.fx += at.x
        if (d.fy != null) d.fy += at.y
      }
    }
    tick()
    notifyOverview(true)
  }

  // Build the SVG, zoom and simulation once per nodes/edges set.
  useEffect(() => {
    const svgEl = svgRef.current
    const container = containerRef.current
    if (!svgEl || !container) return
    const measure = () => {
      const rect = container.getBoundingClientRect()
      const w = rect.width || 800, h = rect.height || 600
      sizeRef.current = { w, h }
      svgEl.setAttribute('viewBox', `${-w / 2} ${-h / 2} ${w} ${h}`)
      notifyViewport()
    }
    measure()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    ro?.observe(container)

    const svg = select(svgEl)
    svg.selectAll('*').remove()
    const zoomG = svg.append('g').attr('class', 'zoom')
    zoomG.append('g').attr('class', 'links')
    zoomG.append('g').attr('class', 'effects')
    zoomG.append('g').attr('class', 'nodes')

    const zoomB = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent(zoomExtent())
      // The viewBox is centred on 0,0; saying so here keeps d3-zoom off the
      // SVG viewBox API (absent in jsdom) and makes translateTo/scaleTo exact.
      .extent((): [[number, number], [number, number]] => {
        const { w, h } = sizeRef.current
        return [[-w / 2, -h / 2], [w / 2, h / 2]]
      })
      .filter((event: Event) => event.type !== 'dblclick')
      .on('zoom', (event) => {
        transformRef.current = event.transform
        zoomG.attr('transform', event.transform.toString())
        notifyViewport()
      })
    svg.call(zoomB).on('dblclick.zoom', null)
    zoomRef.current = zoomB
    svg.on('click', () => {
      propsRef.current.onNodeSelect?.(null)
      applyFocus(null)
    })

    const model = buildModel(nodes, edges)
    modelRef.current = model
    // A rebuild that comes with a view switch carries the reader's state over.
    const carry = appliedLayoutRef.current !== propsRef.current.layout ? carryRef.current : null
    carryRef.current = null
    if (carry) for (const d of model.nodes) d.collapsed = carry.collapsed.has(d.id) && d.childIds.length > 0
    const { nodes: vis, links } = visibleGraph(model)
    const sim = createSimulation(vis, links, spacingFactor())
    simRef.current = sim
    selectedRef.current = null
    hoveredRef.current = null
    appliedSpacingRef.current = propsRef.current.spacing

    seedRadialPositions(model, spacingFactor())
    render()
    // Open at 100% (natural size, k = 1) centred on the root, as the design's
    // reference frames are drawn; the minimap's fit button frames all.
    svg.call(zoomB.transform, carry ? carry.transform : zoomIdentity)
    if (propsRef.current.layout === 'tree') {
      layoutTree()
    } else {
      // Settled before it is shown: no entrance, and the camera does not move.
      // A rebuild for a view switch keeps the root where the reader is looking.
      layoutRadial(true, carry ? viewCentre() : undefined)
    }
    // This build is in the current view, laid out: the layout-switch effect
    // has nothing left to do (it would lay the graph out a second time).
    if (appliedLayoutRef.current !== propsRef.current.layout) {
      appliedLayoutRef.current = propsRef.current.layout
      styleLinks()
      if (transformRef.current.k < zoomExtent()[0]) svg.call(zoomB.scaleTo, zoomExtent()[0])
    }
    const keepSelected = selectedNodeIdRef.current
    if (carry && keepSelected && model.byId.has(keepSelected)) applyFocus(keepSelected)
    notifyOverview(true)
    return () => {
      carryRef.current = {
        collapsed: new Set(model.nodes.filter((d) => d.collapsed).map((d) => d.id)),
        transform: transformRef.current,
      }
      ro?.disconnect()
      sim.stop()
      simRef.current = null
      modelRef.current = null
      svg.on('.zoom', null).on('click', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the display props have their own effects
  }, [nodes, edges])

  // Layout switch: tree positions, or back to physics from the current positions.
  useEffect(() => {
    if (appliedLayoutRef.current === layout || !modelRef.current) return
    appliedLayoutRef.current = layout
    zoomRef.current?.scaleExtent(zoomExtent())
    // Back in Radial View from a Tree View zoomed out past Radial's floor: come up to the floor.
    if (svgRef.current && zoomRef.current && transformRef.current.k < zoomExtent()[0]) {
      select(svgRef.current).call(zoomRef.current.scaleTo, zoomExtent()[0])
    }
    if (layout === 'tree') {
      layoutTree()
      styleLinks()
    } else {
      // The radial picture at rest, at once, with the root where the reader
      // is looking, so the view does not have to move.
      styleLinks()
      layoutRadial(true, viewCentre())
    }
    // No fit after a switch: the view stays where the reader left it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout])

  // Spacing: the settled picture is blown up (or shrunk) about the root at
  // once, as the slider moves. The plan scales with it (`radialPlan`), so this
  // is the layout a fresh settle at the new spacing gives, without paying for
  // one on every step; a later collapse or expand settles from it at rest.
  useEffect(() => {
    const model = modelRef.current
    const sim = simRef.current
    const from = appliedSpacingRef.current
    appliedSpacingRef.current = spacing
    // Tree View has its own grid; Radial picks the spacing up when it is next
    // laid out. (No replan here: re-setting the nodes snaps the pinned root
    // back to its radial pin, under the tree.)
    if (!model || !sim || from === spacing || propsRef.current.layout !== 'radial') return
    replan(sim, visibleGraph(model).links, spacing / 100)
    const root = model.byId.get(model.rootIds[0])
    if (!root) return
    const ratio = spacing / from
    const cx = root.x, cy = root.y
    for (const d of model.nodes) {
      d.x = cx + (d.x - cx) * ratio
      d.y = cy + (d.y - cy) * ratio
      if (d.fx != null) d.fx = cx + (d.fx - cx) * ratio
      if (d.fy != null) d.fy = cy + (d.fy - cy) * ratio
    }
    tick()
    notifyOverview(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spacing])

  useEffect(() => {
    applyVisibility()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledTypes, filteredOutIds, showOrphans, keepUnlinked, showEntityLabels, showEdgeLabels, lineWidth])

  // External selection (the details panel's connection chips) focuses too.
  useEffect(() => {
    if (!modelRef.current || selectedRef.current === (selectedNodeId ?? null)) return
    applyFocus(selectedNodeId ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId])

  useEffect(() => {
    if (!modelRef.current) return
    applyFocus(selectedRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLink?.source, selectedLink?.target])

  useImperativeHandle(ref, () => ({
    setZoom: (level) => {
      if (!zoomRef.current || !svgRef.current) return
      const [kMin, kMax] = zoomExtent()
      select(svgRef.current).call(zoomRef.current.scaleTo, Math.max(kMin, Math.min(kMax, level)))
    },
    // Fit to screen snaps into place: the reader asked for no camera animation here.
    fit: () => fitTo(false),
    getZoom: () => transformRef.current.k,
    // 100% is natural size (k = 1): the spec's radii as drawn in the design's
    // reference frames, in both views. Not the fit-to-screen scale, which on
    // a hundred-document tenant shrank "100%" to unreadable.
    getFitZoom: () => 1,
    getMinZoom: () => zoomExtent()[0],
    getMaxZoom: () => zoomExtent()[1],
    subscribe: (fn) => { zoomSubs.current.add(fn); fn(transformRef.current.k); return () => void zoomSubs.current.delete(fn) },
    subscribeViewport: (fn) => { viewportSubs.current.add(fn); fn(viewport()); return () => void viewportSubs.current.delete(fn) },
    subscribeOverview: (fn) => {
      overviewSubs.current.add(fn)
      const ov = buildOverview()
      if (ov) fn(ov)
      return () => void overviewSubs.current.delete(fn)
    },
    focusNode: (id) => {
      if (!modelRef.current?.byId.has(id)) return
      applyFocus(id)
    },
    getOverview: buildOverview,
    replayEntrance: () => {
      if (!modelRef.current || !svgRef.current || !zoomRef.current) return
      selectedRef.current = null
      propsRef.current.onNodeSelect?.(null)
      applyFocus(null)
      select(svgRef.current).call(zoomRef.current.transform, zoomIdentity)
      if (propsRef.current.layout === 'tree') layoutTree()
      else layoutRadial(true)
    },
    collapseToSites: () => {
      if (!modelRef.current) return
      collapseToDepth(modelRef.current, 1)
      render()
      if (propsRef.current.layout === 'tree') layoutTree()
      else layoutRadial(false)
    },
    expandAll: () => {
      if (!modelRef.current) return
      expandAll(modelRef.current)
      render()
      if (propsRef.current.layout === 'tree') layoutTree()
      else layoutRadial(false)
    },
    toggleChildren: (id) => {
      if (!modelRef.current) return false
      const ok = toggleChildren(modelRef.current, id)
      if (!ok) return false
      render()
      if (propsRef.current.layout === 'tree') layoutTree()
      else layoutRadial(false)
      applyFocus(id)
      return true
    },
    isCollapsed: (id) => modelRef.current?.byId.get(id)?.collapsed ?? false,
    hasChildren: (id) => (modelRef.current?.byId.get(id)?.childIds.length ?? 0) > 0,
  }))

  return (
    <div ref={containerRef} className="h-full w-full bg-[#f5f5f4] font-sans dark:bg-neutral-900" data-testid="kg-d3-canvas">
      <svg ref={svgRef} className="block h-full w-full [&_g.node.dimmed]:opacity-15 [&_path.link.dimmed]:opacity-10 [&_g.node.faded:not(.dimmed)]:opacity-30 [&_path.link.faded:not(.dimmed)]:opacity-25 [&_text.link-label.faded]:opacity-25 [&_g.node.near_text.label]:fill-neutral-800 dark:[&_g.node.near_text.label]:fill-neutral-100 [&_g.node.selected_text.label]:fill-[#34728d] [&_g.node.selected_text.label]:font-medium" />
    </div>
  )
})

export default KnowledgeGraphD3Canvas
